import { colors, Indent, Stack, Typography } from '@ui-components/neo.tax';
import * as React from 'react';

import { interpolateTaxYear } from '@neo.tax/rd/utils';
import { Question as QuestionType, AnswerType, InputType } from '@neo.tax/shared';

import { ActionButton } from './ActionButton';
import { ButtonField } from './ButtonField';
import { CheckboxesField } from './CheckboxesField';
import { BadAnswerTypeForInputTypeError } from './company-info.errors';
import { QuestionProps } from './company-info.types';
import { DropdownFieldNumber } from './DropdownFieldNumber';
import { DropdownFieldState } from './DropdownFieldState';
import { DropdownFieldString } from './DropdownFieldString';
import { DropdownFieldYear } from './DropdownFieldYear';
import { MonthYearField } from './MonthYearField';
import { TextareaField } from './TextareaField';
import { TextField } from './TextField';

const fieldComponent = (question: QuestionType): React.ComponentType<QuestionProps> | null => {
  switch (question.inputType) {
    case InputType.Input:
    case InputType.DatePicker:
      return TextField;
    case InputType.Dropdown:
      switch (question.answerType) {
        case AnswerType.String:
          return DropdownFieldString;
        case AnswerType.State:
          return DropdownFieldState;
        case AnswerType.Number:
          return DropdownFieldNumber;
        case AnswerType.Year:
          return DropdownFieldYear;
        default:
          throw new BadAnswerTypeForInputTypeError(question);
      }

    case InputType.Button:
      switch (question.answerType) {
        case AnswerType.Boolean:
          return ButtonField;
        default:
          throw new BadAnswerTypeForInputTypeError(question);
      }

    case InputType.Checkbox:
      return CheckboxesField;

    case InputType.MonthYear:
      return MonthYearField;

    case InputType.Textarea:
      return TextareaField;

    case InputType.Upload:
      return null;
  }
};

export const Question: React.FC<QuestionProps> = ({
  actionButtonModal,
  actionButtonProps,
  question,
  shortYearEndMonthYear,
  shortYearStartMonthYear,
  taxYear,
}) => {
  const { displaySubtext, displayText, inputType } = question;
  const gap = inputType === InputType.Checkbox ? 4 : 1;
  const FieldComponent = fieldComponent(question);

  const choicesWithChildrenToShow =
    question.questionChoices?.filter((choice) => choice.selected && choice.childQuestions) || [];

  return (
    // Skip `null` FieldComponents. These are ones we've decided not to support
    // or display right now.
    FieldComponent && (
      <Stack direction="column" gap={6} fullWidth>
        <Stack direction="column" gap={gap} fullWidth>
          <Stack gap={2} alignItems="end" justifyContent="space-between" fullWidth>
            <Typography fontSize="sm" fontWeight="medium" color={colors.gray[700]}>
              {interpolateTaxYear(displayText, taxYear)}
            </Typography>
            {actionButtonProps && actionButtonProps.position === 'topRight' && <ActionButton {...actionButtonProps} />}
          </Stack>
          <FieldComponent
            question={question}
            taxYear={taxYear}
            // Temporary fix for MonthYear. This should be removed once the short year question is combined into one (NEO-5604)
            shortYearStartMonthYear={shortYearStartMonthYear}
            shortYearEndMonthYear={shortYearEndMonthYear}
          />
          {displaySubtext && (
            <Typography color={colors.gray[500]} fontSize="sm" fontWeight="normal">
              {displaySubtext}
            </Typography>
          )}
        </Stack>

        {choicesWithChildrenToShow.length > 0 && (
          <Indent>
            {choicesWithChildrenToShow.map((choice) => (
              <React.Fragment key={choice.id}>
                {choice.childQuestions?.map((child) => (
                  <Question
                    key={child.id}
                    question={child}
                    taxYear={taxYear}
                    // Temporary fix for MonthYear. This should be removed once the short year question is combined into one (NEO-5604)
                    shortYearStartMonthYear={shortYearStartMonthYear}
                    shortYearEndMonthYear={shortYearEndMonthYear}
                  />
                ))}
              </React.Fragment>
            ))}
          </Indent>
        )}
        {actionButtonProps && actionButtonProps.position === 'bottom' && (
          <ActionButton
            {...actionButtonProps}
            onClick={
              actionButtonProps.actionButtonModalProps
                ? actionButtonProps.actionButtonModalProps.openModal
                : actionButtonProps.onClick
            }
          />
        )}
        {actionButtonModal}
      </Stack>
    )
  );
};
